import React, { useEffect, useRef, useState } from 'react';
import fscreen from 'fscreen';
import Backdrop from '@material-ui/core/Backdrop';
import CircularProgress from '@material-ui/core/CircularProgress';
import SectionEngagement from '../../components/SectionEngagement/SectionEngagement';
import Video from '../../components/Video/Video';
import V2Slideshow from '../components/V2Slideshow/V2Slideshow';
import V2Video from '../components/V2Video/V2Video';
import { useV2PresentationLoad } from '../../hooks/useV2PresentationLoad';
import { canUseAudioDescribed } from '../../utils/audioDescribedInternalOnly';
import { mapPresentationJsonToPlayerState } from '../../utils/v2PresentationApi';
import './V2Container.css';

const SLIDE_SRC_BASE_URL = 'https://images.flatworldknowledge.com/burger';

function generateSlideSrc(index, slides, internalId) {
  const src = slides[index] && slides[index].src;
  return src
    ? `${SLIDE_SRC_BASE_URL}/${internalId}/${src}`
    : '';
}

export default function V2Container(props) {
  const {
    currentUser,
    toc,
    bookId,
    elementId,
    internalId,
    location = {},
    textbookTitle,
  } = props;

  const [slides, setSlides] = useState(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(null);
  const [currentSlideSrc, setCurrentSlideSrc] = useState(null);
  const [fullscreen, setFullscreen] = useState(null);
  const [fullscreenAvailable, setFullscreenAvailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [multiPart, setMultiPart] = useState(null);
  const [multiPartDuration, setMultiPartDuration] = useState(0);
  const [videoAspectRatio, setVideoAspectRatio] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);

  const containerRef = useRef();
  const slideshowRef = useRef();
  const videoRef = useRef();

  useEffect(() => {
    setFullscreenAvailable(fscreen.fullscreenEnabled && !/webkit/.test(fscreen.fullscreenPseudoClass));

    function handleFullscreenchange() {
      const el = fscreen.fullscreenElement;
      if (el) {
        const containerEl = containerRef.current;
        setFullscreen(el === containerEl ? 'slideshow' : 'video');
      } else {
        setFullscreen(null);
      }
      slideshowRef.current.resetZoom();
      videoRef.current.resetMinimized();
    }

    fscreen.onfullscreenchange = handleFullscreenchange;
  }, []);

  const audioDescribedEnabled = canUseAudioDescribed(currentUser);

  const handleFetchDataError = () => {
    setSlides([]);
    setCurrentSlideSrc('');
    setVideoUrl('');
    setMultiPart(false);
    setLoading(false);
  };

  const {
    preferAudioDescribed,
    setPreferAudioDescribed,
    audioDescribedMetadataExists,
    presentationLoadRefs,
  } = useV2PresentationLoad({
    internalId,
    bookId,
    elementId,
    audioDescribedEnabled,
    locationSearch: location.search,
    onLoadStart: () => {
      setReady(false);
      setLoading(true);
    },
    onPresentationLoaded: ({ presentationResult }) => {
      const player = mapPresentationJsonToPlayerState(presentationResult.data);
      if (player.kind === 'multipart') {
        setMultiPart(true);
        setMultiPartDuration(player.multiPartDuration);
        setReady(true);
        return;
      }
      setVideoUrl(player.videoUrl);
      setVideoAspectRatio(player.videoAspectRatio);
      setSlides(player.slides);
      setMultiPart(false);
    },
    onFetchError: handleFetchDataError,
  });

  useEffect(() => {
    if (!slides) { return; }

    function initSlides() {
      const currentSlideIndex = 0;
      const currentSlideSrc = generateSlideSrc(
        currentSlideIndex,
        slides,
        presentationLoadRefs.current.internalId,
      );

      setCurrentSlideIndex(currentSlideIndex);
      setCurrentSlideSrc(currentSlideSrc);
      setLoading(false);
    }

    initSlides();
  }, [slides, presentationLoadRefs]);

  const generateCurrentSlideIndex = (currentTime) => {
    // Find next slide index via current timestamp
    const nextIndex = slides.findIndex(s => +s.time >= currentTime);

    let updatedIndex;
    if (nextIndex === -1) {
      // If no nextIndex, set updatedIndex as last in array
      updatedIndex = slides.length - 1;
    } else {
      updatedIndex = (+slides[nextIndex].time === currentTime)
        ? nextIndex // Exact timestamp
        : nextIndex - 1; // After timestamp, set as previous index
    }

    // Update state only when necessary
    if (updatedIndex !== currentSlideIndex) {
      const updatedSlideSrc = generateSlideSrc(updatedIndex, slides, internalId);
      setCurrentSlideIndex(updatedIndex);
      setCurrentSlideSrc(updatedSlideSrc);
    }
  };

  const handleToggleFullscreenSlideshow = () => {
    if (!fullscreenAvailable) { return; }
    const containerEl = containerRef.current;

    if (!fullscreen) {
      if (fscreen.requestFullscreenFunction(containerEl)) { fscreen.requestFullscreen(containerEl); }
    } else {
      if (fscreen.fullscreenElement) { fscreen.exitFullscreen(); }
    }
  };

  const handleVideoError = () => {
    setReady(true);
  };

  const handleVideoProgress = (progressState) => {
    generateCurrentSlideIndex(progressState.playedSeconds);
  };

  const handleVideoReady = () => {
    setReady(true);
  };

  const renderContent = () => {
    if (multiPart) {
      return (
        <Video
          bookId={bookId}
          currentUser={currentUser}
          duration={multiPartDuration}
          elementId={elementId}
          textbookTitle={textbookTitle}
        />
      );
    }

    if (!loading) {
      return (
        <div className="V2Container-main">
          <V2Video
            aspectRatio={videoAspectRatio}
            audioDescribedAvailable={audioDescribedEnabled && audioDescribedMetadataExists}
            bookId={bookId}
            currentUser={currentUser}
            elementId={elementId}
            fullscreen={fullscreen}
            preferAudioDescribed={preferAudioDescribed}
            textbookTitle={textbookTitle}
            url={videoUrl}
            onAudioDescribedToggle={setPreferAudioDescribed}
            onError={handleVideoError}
            onProgress={handleVideoProgress}
            onReady={handleVideoReady}
            ref={videoRef}
          />
          <V2Slideshow
            fullscreen={fullscreen}
            fullscreenAvailable={fullscreenAvailable}
            src={currentSlideSrc}
            onToggleFullscreenSlideshow={handleToggleFullscreenSlideshow}
            ref={slideshowRef}
          />
        </div>
      );
    }
  };

  return (
    <div
      className={'V2Container ' + (fullscreen ? 'fullscreen' : '')}
      ref={containerRef}
    >
      {renderContent()}
      <Backdrop
        className="Backdrop"
        open={!ready}
        transitionDuration={{ appear: 400, exit: 1000 }}>
        <CircularProgress color="primary" size="8vmax" />
      </Backdrop>
      {currentUser && (
        <SectionEngagement
          toc={toc}
          bookId={bookId}
          itemId={elementId}
          textbookTitle={textbookTitle}
        />
      )}
    </div>
  );
}
